import { useAuth } from '../auth/AuthProvider';
import type { Tenant } from '../auth/types';
import { getRuntimeConfig } from '../config/runtime-config';

/**
 * How the chrome PRESENTS the signed-in tenant (SDD-022 D-5, D-8, T-53).
 *
 * The tenant itself comes from the token and never from config — that does not change. What an
 * environment may add is a presented name and a demo notice for a tenant id
 * (`chrome.demoTenants`), so a demo tenant can be shown as the client it is demonstrated to
 * without touching the realm. Precedence: a name the token names (`custom:tenant_name`, the
 * legacy session) → the environment's presented name → the token's slug.
 */
export interface TenantPresentation {
  /** The token's tenant id, or null when nobody is signed in. */
  readonly tenantId: string | null;
  /** The name the chrome shows, or null when nobody is signed in. */
  readonly name: string | null;
  /** Where the shown name came from — the badge's tooltip says so. */
  readonly nameSource: 'token' | 'environment' | null;
  /** Whether the demo notice opens once per browser session for this tenant. */
  readonly notice: boolean;
}

export type DemoTenants = Readonly<
  Record<string, { readonly name: string; readonly notice: boolean }>
>;

export function tenantPresentationOf(
  tenant: Tenant | null,
  demoTenants: DemoTenants,
): TenantPresentation {
  if (tenant === null) return { tenantId: null, name: null, nameSource: null, notice: false };
  const configured = Object.prototype.hasOwnProperty.call(demoTenants, tenant.id)
    ? demoTenants[tenant.id]
    : undefined;
  if (configured !== undefined && tenant.nameFromClaim !== true) {
    return {
      tenantId: tenant.id,
      name: configured.name,
      nameSource: 'environment',
      notice: configured.notice,
    };
  }
  return {
    tenantId: tenant.id,
    name: tenant.name,
    nameSource: 'token',
    notice: configured?.notice ?? false,
  };
}

/** Read at render time, so Storybook and tests can inject a config after the bundle loaded. */
export function useTenantPresentation(): TenantPresentation {
  const { tenant } = useAuth();
  return tenantPresentationOf(tenant, getRuntimeConfig().chrome.demoTenants);
}
